// src/components/UploadPage.jsx

import BASE_URL from '../config';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import './UploadPage.css';

// Core assets
import logoImg from '../assets/M2_Lab_Logo.png';
import uploadIcon from '../assets/upload.png';
import xceptionIcon from '../assets/xception.png';
import vitIcon from '../assets/vit.png';
import uploadBg from '../assets/upload_background.png';
import metadataIcon from '../assets/face_detection.png';
import vitIcon1 from '../assets/vit1.png';
import f3netIcon from '../assets/f3net.png';
import dagfddIcon from '../assets/dagfdd.png';
import dawfddIcon from '../assets/dawfdd.png';
import coreIcon from '../assets/core.png';
import audioIcon1 from '../assets/audio1.png';
import audioPreviewIcon from '../assets/audio-file.png';
import spslIcon from '../assets/spsl.png';
import srmIcon from '../assets/srm.png';
import ucfIcon from '../assets/ucf.png';
import efficientIcon from '../assets/efficient.png';
import univfdIcon from '../assets/univfd.png';

// New icons
import tokenIcon from '../assets/token.png';
import imageIcon from '../assets/images.png';
import videoIcon from '../assets/video.png';
import audioIcon from '../assets/audio.png';

// Toastify
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Spinner
import Spinner from './Spinner';

/* ------------------------------
   METRICS HELPERS (simple fetch)
---------------------------------*/
const metricsPost = (path, body) =>
  fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include'
  }).catch(() => { /* non-blocking */ });

const logModelClick = (model_name) =>
  metricsPost('/metrics/model-click', { model_name });

const logCategoryClick = (category) =>
  metricsPost('/metrics/category-click', { category });

const logUpload = (media_type /* image|audio */) =>
  metricsPost('/metrics/upload', { media_type });

const logPrediction = (label /* real|fake */) =>
  metricsPost('/metrics/prediction', { label });

/* ------------------------------
   MODEL → CATEGORY MAP
---------------------------------*/
const MODEL_CATEGORY = {
  // Naive
  'XceptionNet': 'Naive',
  'EfficientNetB4': 'Naive',
  'ViT-B/16': 'Naive',
  // Frequency
  'F3Net': 'Frequency',
  'SPSL': 'Frequency',
  'SRM': 'Frequency',
  // Fairness Enhanced
  'DAG-FDD': 'Fairness',
  'DAW-FDD': 'Fairness',
  'PGFDD': 'Fairness',
  // Spatial
  'UCF': 'Spatial',
  'Core': 'Spatial',
  'UnivFD': 'Spatial',
  // Audio
  'AudioFakeDetector': 'Audio'
};

/* ------------------------------
   Category tooltips (short, user-facing)
---------------------------------*/
const CATEGORY_TIPS = {
  Naive:
    'Baseline detectors that look for common visual patterns and inconsistencies in deepfakes.',
  Frequency:
    'Analyze pixel frequencies and hidden noise to spot artifacts left by deepfake generation.',
  'Fairness Enhanced':
    'Designed to reduce bias and perform equally well across different ages, genders, and ethnicities.',
  Spatial:
    'Examine fine image details (faces, edges, textures) to detect subtle manipulations.',
  Audio:
    'Focus on voice recordings to identify synthetic or tampered speech.'
};


function UploadPage() {
  const [activeLink, setActiveLink] = useState('Submissions');
  const [credits, setCredits] = useState(0);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [consentSelection, setConsentSelection] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [imageInfo, setImageInfo] = useState({ type: '', size: '', resolution: '' });
  const [descExpanded, setDescExpanded] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Verify session and fetch credits on mount
  useEffect(() => {
    const verifySessionAndFetchCredits = async () => {
      try {
        const res = await fetch(`${BASE_URL}/verify-session`, {
          method: 'GET',
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          const email = data.email;
          localStorage.setItem('userEmail', email);

          const creditRes = await fetch(`${BASE_URL}/get-credits?email=${encodeURIComponent(email)}`);
          if (creditRes.ok) {
            const creditData = await creditRes.json();
            setCredits(creditData.credits);
          } else {
            toast.error('Failed to load credits');
          }
        } else {
          toast.error("Session expired. Please login again.");
          setTimeout(() => navigate('/login'), 2000);
        }
      } catch {
        toast.error("Error verifying session.");
        setTimeout(() => navigate('/login'), 2000);
      }
    };
    verifySessionAndFetchCredits();
  }, [navigate]);

  useEffect(() => { setDescExpanded(false); }, [selectedModel, analysis]);

  const handleUploadClick = () => fileInputRef.current.click();

  // AUDIO ALERT TOAST NOTIFICATION
  const showAudioAlert = () => {
    toast.info(
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img src={audioIcon} alt="Audio" style={{ width: 24, height: 24, marginRight: 8 }} />
        <span style={{ fontWeight: 'bold' }}>
          Audio file detected. Please click on <strong>Audio ▼</strong>.
        </span>
      </div>,
      { autoClose: 5000, className: 'glowing-audio-toast' }
    );
  };

  const handleDrop = e => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleImage(file);
  };

  const handleFileUpload = e => {
    const file = e.target.files[0];
    if (file) handleImage(file);
  };

  const handleImage = file => {
    setSelectedModel(null);
    setAnalysis(null);
    setConsentSelection(true);
    setShowConsent(false);

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewURL(url);

    // ---- Metrics: log upload type (image/audio) ----
    if (file.type.startsWith('audio/')) {
      logUpload('audio');
      showAudioAlert();
    } else if (file.type.startsWith('image/')) {
      logUpload('image');
    }
    // (If you later want to track video, add another endpoint/counter.)

    const type = file.type.split('/')[1]?.toUpperCase() || 'Unknown';
    const size = `${(file.size / 1024).toFixed(2)} KB`;
    const img = new Image();
    img.onload = () => setImageInfo({ type, size, resolution: `${img.width} x ${img.height}` });
    img.src = url;
  };

  // CLEAR BUTTON
  const handleClearMedia = () => {
    setSelectedFile(null);
    setPreviewURL(null);
    setSelectedModel(null);
    setAnalysis(null);
    setConsentSelection(null);
    setShowConsent(false);
    setImageInfo({ type: '', size: '', resolution: '' });
  };

  // LOGOUT
  const handleLogout = async () => {
    try {
      await fetch(`${BASE_URL}/logout`, { method: 'POST', credentials: 'include' });
      localStorage.removeItem('userEmail');
      toast.success("Logged out successfully");
      setTimeout(() => navigate('/login'), 1000);
    } catch {
      toast.error("Logout failed");
    }
  };

  // ---- Model select + Metrics (model & category) ----
  const handleModelSelect = (model) => {
    setSelectedModel(model);
    // non-blocking logs
    logModelClick(model);
    const category = MODEL_CATEGORY[model];
    if (category) logCategoryClick(category);
  };

  const handleDetect = () => {
    if (!selectedFile || !selectedModel) {
      toast.warn('Please select a model and upload media first.');
      return;
    }
    setShowConsent(true);
  };

  // Decide overall label for metrics (simple rule)
  const overallLabelFromResult = (result, modelName) => {
    if (!result) return null;
    if (modelName === 'AudioFakeDetector') {
      const l = (result.prediction || '').toLowerCase();
      return (l === 'real' || l === 'fake') ? l : null;
    }
    // image/multiface: majority vote
    if (Array.isArray(result.faces) && result.faces.length > 0) {
      let fake = 0, real = 0;
      for (const f of result.faces) {
        const l = (f.prediction || '').toLowerCase();
        if (l === 'fake') fake += 1; else if (l === 'real') real += 1;
      }
      if (fake === 0 && real === 0) return null;
      return fake > real ? 'fake' : 'real';
    }
    return null;
  };

  // HANDLE CONSENT CONFIRMATION V1
  const handleConsentConfirm = async () => {
    if (consentSelection === null) {
      toast.warn('Please select Yes or No before confirming.');
      return;
    }
    setShowConsent(false);

    if (credits <= 0) {
      toast.error('All credits used. For more credits, please contact m2lab.purdue@gmail.com', { autoClose: false });
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('model', selectedModel);

    setLoading(true);

    try {
      const endpoint =
        selectedModel === 'AudioFakeDetector'
          ? `${BASE_URL}/predict-audio`
          : `${BASE_URL}/predict-multiface`;

      const res = await fetch(endpoint, { method: 'POST', body: formData });
      const result = await res.json();

      if (!res.ok) {
        toast.error(result.message || 'Prediction failed.');
        return;
      }

      // decrement credit
      const email = localStorage.getItem('userEmail');
      if (email) {
        const dec = await fetch(`${BASE_URL}/decrement-credit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        if (dec.ok) {
          const data = await dec.json();
          setCredits(data.credits);
        }
      }

      setAnalysis(result);

      if (selectedModel === 'AudioFakeDetector') {
        setPreviewURL(null);
      } else {
        setPreviewURL(`data:image/jpeg;base64,${result.original_image}`);
      }

      // existing analytics
      const userEmail = localStorage.getItem('userEmail');
      fetch(`${BASE_URL}/log-activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          activity_type: 'Prediction',
          details: `Predicted using ${selectedModel}`
        })
      }).catch(() => {});

      fetch(`${BASE_URL}/log-file-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          filename: selectedFile.name,
          file_type: imageInfo.type,
          file_size: imageInfo.size,
          resolution: imageInfo.resolution
        })
      }).catch(() => {});

      // ---- Metrics: log overall label (real/fake) ----
      const overall = overallLabelFromResult(result, selectedModel);
      if (overall === 'real' || overall === 'fake') {
        logPrediction(overall);
      }

    } catch (error) {
      toast.error('Prediction failed. Please check backend.');
      console.error('Prediction or logging failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // RenderProgress
  const renderProgress = (confidence, label, idx) => {
    const isFake = label.toLowerCase() === 'fake';
    confidence = Math.min(100, Math.max(0, parseFloat(confidence)));

    const radius = 50;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - confidence / 100);

    return (
      <svg width="120" height="120" title={isFake ? 'DeepFake confidence' : 'Real confidence'}>
        <circle cx="60" cy="60" r={radius} stroke="#222" strokeWidth="10" fill="none" />
        <defs>
          <linearGradient id={`grad-${idx}`} x1="0" y1="0" x2="1" y2="0">
            {isFake ? (
              <>
                <stop offset="0%" stopColor="#ff1f4c" />
                <stop offset="100%" stopColor="#ff9900" />
              </>
            ) : (
              <>
                <stop offset="0%" stopColor="#00ff88" />
                <stop offset="100%" stopColor="#0077ff" />
              </>
            )}
          </linearGradient>
        </defs>
        <circle
          cx="60"
          cy="60"
          r={radius}
          stroke={`url(#grad-${idx})`}
          strokeWidth="10"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
        />
        <text x="60" y="50" textAnchor="middle" fill="#fff" fontSize="15" fontWeight="bold">
          {confidence.toFixed(1)}%
        </text>
        <text x="60" y="70" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold">
          {label.toUpperCase()}
        </text>
      </svg>
    );
  };

  // renderDescription (unchanged content)
  const renderDescription = () => {
    const descText = {
      XceptionNet:
        'Xception is a deep convolutional neural network architecture based on depthwise separable convolutions, reducing parameters and computation while maintaining high performance. It is widely used as a backbone in deepfake detection.',
      PGFDD:
        'PG-FDD (Preserving Generalization Fair Deepfake Detection) employs disentanglement learning to extract demographic and domain-agnostic forgery features, promoting fair learning across a flattened loss landscape...',
      'ViT-B/16':
        'ViT-B/16 is a model that applies the transformer architecture...',
      F3Net:
        'F3Net utilizes a cross-attention two-stream network...',
      'DAG-FDD':
        'DAG-FDD - A demographic-agnostic Fair Deepfake Detection (DAG-FDD)...',
      'DAW-FDD':
        'DAW-FDD - A demographic-aware Fair Deepfake Detection (DAW-FDD)...',
      'CORE':
        ' CORE is a explicitly enforces the consistency of different representations...',
      AudioFakeDetector:
        'AudioFakeDetector leverages RawNet2 and Conditional U-Net...',
      'SPSL':
        ' SPSL integrates spatial image data with the phase spectrum...',
      'SRM':
        'SRM extracts high-frequency noise features...',
      'UCF':
        'UCF presents a multi-task disentanglement framework...',
      'EfficientNetB4':
        ' Efficient B4 is part of the EfficientNet family...'
    };

    if (!selectedModel || !descText[selectedModel]) return null;
    const full = descText[selectedModel];
    const firstLine = full.split('. ')[0] + '.';

    return (
      <p className="model-desc">
        {descExpanded ? full : firstLine}
        {!descExpanded && (
          <span className="more-link" onClick={() => setDescExpanded(true)}>
            {' '}... more
          </span>
        )}
      </p>
    );
  };

  return (
    <>
      {loading && <Spinner />}
      <div className="upload-wrapper">
        <ToastContainer />

        <Header activeLink={activeLink} onNavClick={(item) => setActiveLink(item)} />


        {/* Main Content */}
        <div className="upload-container" style={{ backgroundImage: `url(${uploadBg})` }}>
          <main className="detector-section">

            {/* Credits Remaining */}
            <div className="credits-remaining">
              <div className="credit-icon-box">
                <img src={tokenIcon} alt="Credits" className="token-icon" />
                <p className="icon-label">Credits</p>
              </div>
              <span className="credits-text">{credits}</span>
            </div>

            <h2 className="section-title gradient-text">Synthguard</h2>
            <div className="upload-panel">

              {/* Upload Box */}
              <div className="upload-box">
                <label htmlFor="mediaUpload">Media</label>
                <div
                  className="drop-zone"
                  onClick={handleUploadClick}
                  onDragOver={e => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  {selectedFile ? (
                    selectedFile.type.startsWith('audio/') ? (
                      <img src={audioPreviewIcon} alt="Audio Preview" className="preview-image" />
                    ) : (
                      <img
                        src={previewURL}
                        alt="Preview"
                        className="preview-image"
                        onClick={e => { e.stopPropagation(); setModalImage(previewURL); }}
                      />
                    )
                  ) : (
                    <>
                      <img src={uploadIcon} alt="Upload" className="upload-icon pulse" />
                      <p>
                        Drop Media Here
                        <br />
                        Or
                        <br />
                        Click to Upload
                      </p>
                    </>
                  )}
                </div>

                <input
                  type="file"
                  id="mediaUpload"
                  accept="image/*,video/*,audio/*"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                />

                {selectedFile && (
                  <div className="image-info">
                    <p><strong>Type:</strong> {imageInfo.type}</p>
                    <p><strong>Size:</strong> {imageInfo.size}</p>
                    <p><strong>Resolution:</strong> {imageInfo.resolution}</p>
                  </div>
                )}

                {selectedFile && (
                  <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <button className="clear-media-btn" onClick={handleClearMedia}>
                      Clear Media
                    </button>
                  </div>
                )}

                <div className="allowed-formats">
                  <p>Allowable Formats</p>
                  <div className="format-icons">
                    <div className="format-icon">
                      <img src={imageIcon} alt="Images" title="Images" />
                      <p className="icon-label">Image</p>
                    </div>
                    <div className="format-icon">
                      <img src={videoIcon} alt="Videos" title="Videos" />
                      <p className="icon-label">Video</p>
                    </div>
                    <div className="format-icon">
                      <img src={audioIcon} alt="Audio" title="Audio" />
                      <p className="icon-label">Audio</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Model & Detect Box */}
              <div className="model-box">
                {!analysis ? (
                  <>
                    <button className="select-model-btn">Select AI Model</button>

                    <div className="model-icons">
                      {/* Naive */}
                      <div className="model-category">
                        <h4 className="category-title">
  Naive
  <span className="info-tip" data-tip={CATEGORY_TIPS['Naive']} aria-label="About Naive">?</span>
</h4>

                        <div className="model-row">
                          <div className={`model-icon ${selectedModel === 'XceptionNet' ? 'active' : ''}`} onClick={() => handleModelSelect('XceptionNet')}>
                            <img src={xceptionIcon} alt="Xception" />
                            <p className="model-label">Xception</p>
                          </div>
                          <div className={`model-icon ${selectedModel === 'EfficientNetB4' ? 'active' : ''}`} onClick={() => handleModelSelect('EfficientNetB4')}>
                            <img src={efficientIcon} alt="Efficient" />
                            <p className="model-label">Efficient</p>
                          </div>
                          <div className={`model-icon ${selectedModel === 'ViT-B/16' ? 'active' : ''}`} onClick={() => handleModelSelect('ViT-B/16')}>
                            <img src={vitIcon1} alt="ViT-B/16" />
                            <p className="model-label">ViT-B/16</p>
                          </div>
                        </div>
                      </div>

                      {/* Frequency */}
                      <div className="model-category">
                        <h4 className="category-title">
  Frequency
  <span className="info-tip" data-tip={CATEGORY_TIPS['Frequency']} aria-label="About Frequency">?</span>
</h4>

                        <div className="model-row">
                          <div className={`model-icon ${selectedModel === 'F3Net' ? 'active' : ''}`} onClick={() => handleModelSelect('F3Net')}>
                            <img src={f3netIcon} alt="F3Net" />
                            <p className="model-label">F3Net</p>
                          </div>
                          <div className={`model-icon ${selectedModel === 'SPSL' ? 'active' : ''}`} onClick={() => handleModelSelect('SPSL')}>
                            <img src={spslIcon} alt="SPSL" />
                            <p className="model-label">SPSL</p>
                          </div>
                          <div className={`model-icon ${selectedModel === 'SRM' ? 'active' : ''}`} onClick={() => handleModelSelect('SRM')}>
                            <img src={srmIcon} alt="SRM" />
                            <p className="model-label">SRM</p>
                          </div>
                        </div>
                      </div>

                      {/* Fairness Enhanced */}
                      <div className="model-category">
                        <h4 className="category-title">
  Fairness Enhanced
  <span className="info-tip" data-tip={CATEGORY_TIPS['Fairness Enhanced']} aria-label="About Fairness Enhanced">?</span>
</h4>

                        <div className="model-row">
                          <div className={`model-icon ${selectedModel === 'DAG-FDD' ? 'active' : ''}`} onClick={() => handleModelSelect('DAG-FDD')}>
                            <img src={dagfddIcon} alt="DAG-FDD" />
                            <p className="model-label">DAG-FDD</p>
                          </div>
                          <div className={`model-icon ${selectedModel === 'DAW-FDD' ? 'active' : ''}`} onClick={() => handleModelSelect('DAW-FDD')}>
                            <img src={dawfddIcon} alt="DAW-FDD" />
                            <p className="model-label">DAW-FDD</p>
                          </div>
                          <div className={`model-icon ${selectedModel === 'PGFDD' ? 'active' : ''}`} onClick={() => handleModelSelect('PGFDD')}>
                            <img src={vitIcon} alt="PGFDD" />
                            <p className="model-label">PGFDD</p>
                          </div>
                        </div>
                      </div>

                      {/* Spatial */}
                      <div className="model-category">
                      <h4 className="category-title">
  Spatial
  <span className="info-tip" data-tip={CATEGORY_TIPS['Spatial']} aria-label="About Spatial">?</span>
</h4>

                        <div className="model-row">
                          <div className={`model-icon ${selectedModel === 'UCF' ? 'active' : ''}`} onClick={() => handleModelSelect('UCF')}>
                            <img src={ucfIcon} alt="UCF" />
                            <p className="model-label">UCF</p>
                          </div>
                          <div className={`model-icon ${selectedModel === 'Core' ? 'active' : ''}`} onClick={() => handleModelSelect('Core')}>
                            <img src={coreIcon} alt="Core" />
                            <p className="model-label">CORE</p>
                          </div>
                          <div className={`model-icon ${selectedModel === 'UnivFD' ? 'active' : ''}`} onClick={() => handleModelSelect('UnivFD')}>
                            <img src={univfdIcon} alt="UnivFD" />
                            <p className="model-label">UnivFD</p>
                          </div>
                        </div>
                      </div>

                      {/* Audio */}
                      <div className="model-category">
                      <h4 className="category-title">
  Audio
  <span className="info-tip" data-tip={CATEGORY_TIPS['Audio']} aria-label="About Audio">?</span>
</h4>

                        <div className="model-row">
                          <div className={`model-icon ${selectedModel === 'AudioFakeDetector' ? 'active' : ''}`} onClick={() => handleModelSelect('AudioFakeDetector')}>
                            <img src={audioIcon1} alt="Audio" />
                            <p className="model-label">AUDIO</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <button className="detect-btn" onClick={handleDetect}>Detect</button>

                    {showConsent && (
                      <div className="popup-consent">
                        <div className="popup-content">
                          <p className="consent-text">Do you allow us to collect the media data you share with us?</p>
                          <div className="radio-buttons">
                            <label className="radio-option yes">
                              <input type="radio" name="consent" onChange={() => setConsentSelection(true)} checked={consentSelection === true} />{' '}YES
                            </label>
                            <label className="radio-option">
                              <input type="radio" name="consent" onChange={() => setConsentSelection(false)} checked={consentSelection === false} />{' '}NO
                            </label>
                          </div>
                          <div className="popup-buttons">
                            <button className="confirm-btn" onClick={handleConsentConfirm}>Confirm</button>
                          </div>
                          <img src={metadataIcon} alt="metadata" className="metadata-popup-icon" />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="analysis-section">
                    <h3 className="gradient-text">Analysis</h3>
                    <p className="model-info">
                      Model Used: <span style={{ color: '#fff' }}>{selectedModel}</span>
                    </p>

                    {selectedModel === 'AudioFakeDetector' ? (
                      <div className="audio-result">
                        <h4>Audio Analysis Result</h4>
                        <p><strong>Prediction:</strong> {analysis.prediction}</p>
                        <p><strong>Confidence:</strong> {analysis.confidence?.toFixed(2)}%</p>
                        <div className="custom-progress">
                          {renderProgress(analysis.confidence, analysis.prediction, 0)}
                        </div>
                      </div>
                    ) : (
                      <div className="multi-face-result">
                        {analysis.faces.map(face => (
                          <div key={face.index} className="face-result-box">
                            <h4>Face {face.index}</h4>
                            <img src={face.face_preview} alt={`Face ${face.index}`} className="face-thumbnail" />
                            <div className="custom-progress">
                              {renderProgress(face.confidence, face.prediction, face.index)}
                            </div>
                            <div className={`prediction-box ${face.prediction.toLowerCase() === 'fake' ? 'fake' : 'real'}`}>
                              {face.prediction.toUpperCase()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <button className="refresh-btn" onClick={() => window.location.reload()}>
                        Continue Analyzing
                      </button>
                    </div>
                    <div className="model-description">{renderDescription()}</div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>

        {/* Image Modal */}
        {modalImage && (
          <div className="image-modal" onClick={() => setModalImage(null)}>
            <div className="modal-content">
              <img src={modalImage} alt="Zoomed Preview" />
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="footer-section">
          <div className="footer-left">© 2025 Purdue University</div>
          <div className="footer-center">
            Developed by Machine Learning and Media Forensics (M2) Lab from Purdue University
          </div>
          <div className="footer-right">
            <a href="#">Privacy Policy</a> | <a href="#">Terms of Use</a> | <a href="#">Contact</a>
          </div>
        </footer>
      </div>
    </>
  );
}

export default UploadPage;
